import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import PageShell from "../../components/public/PageShell";
import IndiaNetworkMap from "../../components/public/IndiaNetworkMap";
import useReveal from "../../hooks/useReveal";
import usePageMeta from "../../hooks/usePageMeta";
import {
  EnvelopeSimple, Phone, MapPin, CheckCircle, PaperPlaneTilt, Clock, BuildingOffice, Crown,
} from "@phosphor-icons/react";
import Captcha from "../../components/public/Captcha";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_OFFICES = [
  {
    name: "Corporate Head Office",
    is_head_office: true,
    address: "Plot 14B, Sector 5 Industrial Area\nBaddi, Solan — 173205\nHimachal Pradesh, India",
    phone: "+91 1795 244 200",
    email: "info@bonheurpharma.in",
    hours: "Mon–Sat · 9:00 AM – 6:00 PM IST",
    map_embed: '<iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3429.0!2d76.793!3d30.962!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzDCsDU3JzQzLjUiTiA3NsKwNDcnMzMuMSJF!5e0!3m2!1sen!2sin!4v1700000000000" width="100%" height="100%" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>',
  },
  { name: "Mumbai Branch Office",   address: "Unit 612, Lotus Corporate Park\nWestern Express Highway, Goregaon East\nMumbai — 400063, Maharashtra", phone: "+91 22 4567 8900",  email: "mumbai@bonheurpharma.in",    hours: "Mon–Fri · 9:30 AM – 6:30 PM IST" },
  { name: "Bengaluru Branch Office",address: "Level 9, Embassy TechVillage\nOuter Ring Road, Devarabisanahalli\nBengaluru — 560103, Karnataka",      phone: "+91 80 4123 5670",  email: "bengaluru@bonheurpharma.in", hours: "Mon–Fri · 9:30 AM – 6:30 PM IST" },
  { name: "Delhi NCR Sales Office", address: "B-405, Spaze i-Tech Park\nSohna Road, Sector 49\nGurugram — 122018, Haryana",                          phone: "+91 124 678 9011", email: "ncr@bonheurpharma.in",       hours: "Mon–Fri · 9:30 AM – 6:30 PM IST" },
];

export default function ContactUs() {
  const [settings, setSettings] = useState({});
  const [offices, setOffices] = useState(DEFAULT_OFFICES);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [flying, setFlying] = useState(false); // paper-plane animation in progress
  const [submitted, setSubmitted] = useState(false);
  const [activeOffice, setActiveOffice] = useState(-1);
  const [captcha, setCaptcha] = useState({ token: "", answer: "" });
  const [captchaKey, setCaptchaKey] = useState(0);
  const cardRefs = useRef([]);
  const { ref: formRef, visible: formVisible } = useReveal();

  usePageMeta({
    title: "Contact — Bon-Heur Pharmaceuticals",
    description: "Manufacturing Unit I & II — Haridwar IIE-SIDCUL · Corporate Office — Mumbai Kalbadevi. Reach Bon-Heur Pharmaceuticals at bdm.bonheurpharma@gmail.com or +91-9520865925.",
    keywords: "Bon-Heur contact, Haridwar pharma, Mumbai pharma office, pharmaceutical enquiry",
  });

  useEffect(() => {
    axios.get(`${API}/public/settings`).then((r) => setSettings(r.data)).catch(() => {});
    axios.get(`${API}/public/pages/home.offices`).then((r) => {
      const list = r.data?.value?.items || r.data?.value?.offices;
      if (Array.isArray(list) && list.length) setOffices(list);
    }).catch(() => {});
  }, []);

  const selectOffice = useCallback((i) => {
    setActiveOffice(i);
    const el = cardRefs.current[i];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.style.animation = "none";
      // force reflow to restart animation
      void el.offsetWidth;
      el.style.animation = "flashHighlight 1.4s cubic-bezier(0.34,1.56,0.64,1) both";
    }
    window.setTimeout(() => setActiveOffice((curr) => (curr === i ? -1 : curr)), 1800);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Name, email and message are required"); return; }
    if (!captcha.token || !captcha.answer) { toast.error("Please solve the CAPTCHA"); return; }
    setSubmitting(true);
    setFlying(true);
    try {
      // Run the paper-plane animation in parallel with the API call so it never feels fake.
      await Promise.all([
        axios.post(`${API}/public/inquiries`, {
          ...form,
          captcha_token: captcha.token,
          captcha_answer: captcha.answer,
        }),
        new Promise((res) => setTimeout(res, 1100)),
      ]);
      setSubmitted(true);
      toast.success("Message sent");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send. Please try again.";
      toast.error(msg);
      setFlying(false);
      setCaptchaKey((k) => k + 1);  // fresh challenge — old one is now single-use consumed on server
    } finally {
      setSubmitting(false);
    }
  };

  const headOffice = offices.find((o) => o.is_head_office) || offices[0];
  const headMap = headOffice?.map_embed || settings.map_embed;
  const social = settings.social_links || {};

  return (
    <PageShell pageKey="contact" eyebrow="Contact" title="Get in touch" subtitle="Whether you're a clinician, distributor or a curious researcher — we'd love to hear from you.">
      {/* FORM + HEAD OFFICE INFO */}
      <section className="py-14 lg:py-20 bg-white" data-testid="contact-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
          {/* Head office card */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>Talk to our team</h2>
            <p className="mt-3 text-[#475569] leading-relaxed">We respond to most enquiries within one business day.</p>
            {headOffice && (
              <div className="mt-8 rounded-2xl border border-[#E2E8F0] p-6 bg-gradient-to-br from-[#F8FAFC] to-white">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#C2410C] font-semibold">
                    <Crown size={14} weight="fill" /> Head office
                  </div>
                  <OpenBadge office={headOffice} />
                </div>
                <h3 className="mt-2 text-lg font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>{headOffice.name || headOffice.title}</h3>
                <div className="mt-4 space-y-4">
                  {headOffice.address && <InfoLine icon={MapPin} label="Address" value={headOffice.address} />}
                  {headOffice.phone && <InfoLine icon={Phone} label="Phone" value={headOffice.phone} href={`tel:${headOffice.phone.replace(/[^+\d]/g, "")}`} />}
                  {headOffice.email && <InfoLine icon={EnvelopeSimple} label="Email" value={headOffice.email} href={`mailto:${headOffice.email}`} />}
                  {headOffice.hours && <InfoLine icon={Clock} label="Hours" value={headOffice.hours} />}
                </div>
              </div>
            )}

            {(social.facebook || social.linkedin || social.instagram || social.twitter) && (
              <div className="mt-8">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#64748B] font-semibold">Follow</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(social).filter(([, v]) => !!v).map(([k, v]) => (
                    <a key={k} href={v} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-full bg-[#F1F5F9] hover:bg-[#C2410C] hover:text-white text-xs font-semibold text-[#475569] transition-colors capitalize">{k}</a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Form */}
          <div ref={formRef} className="lg:col-span-3" style={{ animation: formVisible ? "blurUp 0.7s ease-out 0.1s both" : "none" }}>
            <div className="relative rounded-3xl bg-gradient-to-br from-[#F8FAFC] to-white border border-[#E2E8F0] p-8 lg:p-10 shadow-sm overflow-hidden">
              {submitted ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <CheckCircle size={56} weight="fill" className="mx-auto text-[#0EA5E9]" />
                  <h3 className="mt-4 text-2xl font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>Thank you — we got your message</h3>
                  <p className="mt-3 text-[#475569]">Our team will get back to you shortly.</p>
                </div>
              ) : (
                <>
                  <form onSubmit={submit} className={`space-y-5 transition-opacity duration-500 ${flying ? "opacity-30 pointer-events-none" : "opacity-100"}`} data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input label="Full name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" />
                      <Input label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone" />
                      <Input label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="contact-subject" />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#64748B]">Message *</label>
                      <textarea
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        rows={6}
                        className="mt-1 w-full px-4 py-3 rounded-xl border border-[#E2E8F0] focus:border-[#C2410C] focus:outline-none focus:ring-2 focus:ring-[#C2410C]/20 text-sm transition resize-none"
                        data-testid="contact-message"
                      />
                    </div>
                    <Captcha
                      onChange={setCaptcha}
                      refreshKey={captchaKey}
                      testId="contact-captcha"
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white text-sm font-semibold transition disabled:opacity-60 active:scale-95"
                      data-testid="contact-submit"
                    >
                      {submitting ? "Sending..." : "Send message"} <PaperPlaneTilt size={14} weight="fill" />
                    </button>
                  </form>

                  {/* Paper plane fly-out overlay */}
                  {flying && (
                    <div
                      className="pointer-events-none absolute z-10"
                      style={{
                        left: "12%", bottom: "14%",
                        animation: "paperPlaneFly 1.1s cubic-bezier(0.55, 0.1, 0.25, 1) forwards",
                      }}
                      aria-hidden
                    >
                      <PaperPlaneTilt size={56} weight="fill" className="text-[#C2410C] drop-shadow-[0_8px_24px_rgba(194,65,12,0.45)]" />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* HEAD OFFICE MAP */}
      {headMap && (
        <section className="pb-14 lg:pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl overflow-hidden border border-[#E2E8F0] shadow-sm aspect-[16/8] bg-[#F1F5F9]" data-testid="contact-map" dangerouslySetInnerHTML={{ __html: headMap }} />
            <div className="mt-4 text-xs text-[#64748B] flex items-center gap-2">
              <Crown size={12} weight="fill" className="text-[#C2410C]" /> Showing location of our head office.
            </div>
          </div>
        </section>
      )}

      {/* OFFICES NETWORK MAP + GRID */}
      {offices.length > 0 && (
        <section className="py-14 lg:py-20 bg-[#F8FAFC] border-t border-[#E2E8F0]" data-testid="all-offices">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              {/* Map */}
              <div className="lg:col-span-5 lg:sticky lg:top-24">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#C2410C] font-semibold">Our network</div>
                <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>Find us across India</h2>
                <p className="mt-3 text-[#475569]">Tap any city on the map to jump to its office details.</p>
                <div className="mt-8 rounded-3xl overflow-hidden bg-[#0F172A] p-4 lg:p-6 border border-[#1E293B] shadow-[0_30px_80px_-30px_rgba(15,23,42,0.6)]">
                  <IndiaNetworkMap
                    offices={offices}
                    activeIndex={activeOffice}
                    onSelectOffice={selectOffice}
                  />
                </div>
              </div>

              {/* Office cards */}
              <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
                {offices.map((o, i) => (
                  <OfficeCard
                    key={i}
                    o={o}
                    i={i}
                    setRef={(el) => { cardRefs.current[i] = el; }}
                    isActive={i === activeOffice}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </PageShell>
  );
}

/* ──────────────────────────────────────────────────────────────────────────── */
/* OfficeCard — 3D tilt-on-cursor, live IST clock + Open/Closed badge          */
/* ──────────────────────────────────────────────────────────────────────────── */
function OfficeCard({ o, i, setRef, isActive }) {
  const { ref: revealRef, visible } = useReveal();
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;  // 0..1
    const py = (e.clientY - rect.top) / rect.height;  // 0..1
    const rotY = (px - 0.5) * 12;   // -6..6 deg
    const rotX = (0.5 - py) * 10;   // -5..5 deg
    el.style.transform = `perspective(900px) rotateX(${rotX}deg) rotateY(${rotY}deg) translateY(${visible ? 0 : 16}px)`;
    // Move the glare highlight too
    const glare = el.querySelector("[data-glare]");
    if (glare) glare.style.background = `radial-gradient(220px circle at ${px * 100}% ${py * 100}%, rgba(251,146,60,0.22), transparent 60%)`;
  };
  const handleMouseLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    el.style.transform = `perspective(900px) rotateX(0deg) rotateY(0deg) translateY(${visible ? 0 : 16}px)`;
    const glare = el.querySelector("[data-glare]");
    if (glare) glare.style.background = "transparent";
  };

  const attachRef = (el) => {
    cardRef.current = el;
    revealRef(el);          // hook up the reveal observer too
    if (setRef) setRef(el);
  };

  return (
    <div
      ref={attachRef}
      data-testid={`office-card-${i}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`relative p-6 rounded-2xl bg-white border ${isActive ? "border-[#C2410C]" : "border-[#E2E8F0]"} hover:border-[#C2410C] hover:shadow-xl will-change-transform`}
      style={{
        opacity: visible ? 1 : 0,
        transform: `perspective(900px) translateY(${visible ? 0 : 16}px)`,
        transition: "opacity 700ms, transform 350ms cubic-bezier(0.22,1,0.36,1), border-color 300ms, box-shadow 300ms",
        transitionDelay: `${(i % 6) * 90}ms`,
        transformStyle: "preserve-3d",
      }}
    >
      {/* Glare overlay (mouse-tracked) */}
      <div data-glare className="pointer-events-none absolute inset-0 rounded-2xl transition-[background] duration-150" />

      <div className="relative flex items-start justify-between gap-3" style={{ transform: "translateZ(20px)" }}>
        <div className="w-11 h-11 rounded-xl bg-[#C2410C]/10 flex items-center justify-center flex-shrink-0">
          <BuildingOffice size={20} weight="fill" className="text-[#C2410C]" />
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {o.is_head_office && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FB923C]/10 text-[#C2410C] text-[10px] tracking-[0.2em] uppercase font-semibold">
              <Crown size={10} weight="fill" /> Head
            </span>
          )}
          <OpenBadge office={o} />
        </div>
      </div>
      <h3 className="relative mt-4 text-base font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif", transform: "translateZ(15px)" }}>{o.name || o.title}</h3>
      {o.address && <p className="relative mt-3 text-sm text-[#475569] leading-relaxed whitespace-pre-line" style={{ transform: "translateZ(10px)" }}>{o.address}</p>}
      <div className="relative mt-4 space-y-2 text-sm" style={{ transform: "translateZ(10px)" }}>
        {o.phone && <a href={`tel:${o.phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-2 text-[#374151] hover:text-[#C2410C] transition"><Phone size={13} weight="bold" className="text-[#C2410C]" /> {o.phone}</a>}
        {o.email && <a href={`mailto:${o.email}`} className="flex items-center gap-2 text-[#374151] hover:text-[#C2410C] transition"><EnvelopeSimple size={13} weight="bold" className="text-[#C2410C]" /> {o.email}</a>}
        {o.hours && <div className="flex items-center gap-2 text-[#64748B]"><Clock size={13} weight="bold" className="text-[#94A3B8]" /> {o.hours}</div>}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────── */
/* OpenBadge — live IST clock + Open/Closed indicator parsed from `hours`      */
/* ──────────────────────────────────────────────────────────────────────────── */
function OpenBadge({ office }) {
  const [now, setNow] = useState(() => nowIST());
  useEffect(() => {
    const t = setInterval(() => setNow(nowIST()), 30 * 1000); // refresh every 30s
    return () => clearInterval(t);
  }, []);
  const open = isOfficeOpen(office?.hours, now);
  const timeStr = formatIST(now);
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide ${open ? "bg-[#22C55E]/15 text-[#15803D]" : "bg-[#64748B]/15 text-[#475569]"}`}
      data-testid="office-open-badge"
      title={`Local time (IST): ${timeStr}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${open ? "bg-[#22C55E] animate-pulse" : "bg-[#94A3B8]"}`} />
      {open ? "Open now" : "Closed"} · {timeStr}
    </span>
  );
}

function nowIST() {
  // Force IST (UTC+5:30) regardless of viewer's timezone.
  const d = new Date();
  const utcMs = d.getTime() + d.getTimezoneOffset() * 60 * 1000;
  return new Date(utcMs + 5.5 * 60 * 60 * 1000);
}

function formatIST(d) {
  const h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${ap} IST`;
}

function isOfficeOpen(hoursStr = "", now = nowIST()) {
  if (!hoursStr) return true;
  const day = now.getDay(); // 0=Sun, 1=Mon ...
  // Parse "Mon-Sat" / "Mon–Fri" / "Mon-Fri" + open/close hours
  const dayMap = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
  const dayMatch = hoursStr.match(/(mon|tue|wed|thu|fri|sat|sun)\s*[-–—]\s*(mon|tue|wed|thu|fri|sat|sun)/i);
  if (dayMatch) {
    const a = dayMap[dayMatch[1].toLowerCase()];
    const b = dayMap[dayMatch[2].toLowerCase()];
    let inRange = false;
    if (a <= b) inRange = day >= a && day <= b;
    else inRange = day >= a || day <= b;
    if (!inRange) return false;
  }
  // Parse time range "9:00 AM – 6:00 PM" / "9-6"
  const timeMatch = hoursStr.match(/(\d{1,2})(?::(\d{2}))?\s*([apAP][mM])?\s*[-–—]\s*(\d{1,2})(?::(\d{2}))?\s*([apAP][mM])?/);
  if (!timeMatch) return true;
  const parse12h = (h, m, ap) => {
    let hr = parseInt(h, 10);
    const mn = m ? parseInt(m, 10) : 0;
    if (ap) {
      const isPM = /pm/i.test(ap);
      if (isPM && hr < 12) hr += 12;
      if (!isPM && hr === 12) hr = 0;
    }
    return hr * 60 + mn;
  };
  const openMin = parse12h(timeMatch[1], timeMatch[2], timeMatch[3]);
  const closeMin = parse12h(timeMatch[4], timeMatch[5], timeMatch[6]);
  const curMin = now.getHours() * 60 + now.getMinutes();
  return curMin >= openMin && curMin <= closeMin;
}

function InfoLine({ icon: Icon, label, value, href }) {
  const Inner = (
    <>
      <div className="w-10 h-10 rounded-xl bg-[#C2410C]/10 flex items-center justify-center flex-shrink-0">
        <Icon size={18} weight="fill" className="text-[#C2410C]" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] font-semibold">{label}</div>
        <div className="mt-0.5 text-sm text-[#1F2937] leading-relaxed whitespace-pre-line">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} className="flex items-start gap-3 group hover:translate-x-1 transition-transform">{Inner}</a>
  ) : (
    <div className="flex items-start gap-3">{Inner}</div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#64748B]">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-4 py-3 rounded-xl border border-[#E2E8F0] focus:border-[#C2410C] focus:outline-none focus:ring-2 focus:ring-[#C2410C]/20 text-sm transition"
        data-testid={testid}
      />
    </div>
  );
}
